// Admin operations data: webhook errors, marketplace stats, email health.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(ctx: { supabase: unknown; userId: string }) {
  const sb = ctx.supabase as { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }> };
  const { data, error } = await sb.rpc("has_role", { _user_id: ctx.userId, _role: "admin" });
  if (error || !data) throw new Error("forbidden");
}

export const adminMarketplaceStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ days: z.number().int().min(1).max(365).default(30) }).parse(i ?? {}))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { data: stats, error } = await context.supabase.rpc("admin_marketplace_stats", { _days: data.days });
    if (error) throw new Error(error.message);
    return { stats };
  });

export const adminListWebhookErrors = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("webhook_errors")
      .select("id, source, event_id, event_type, error_message, resolved, resolved_at, created_at")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return { errors: data ?? [] };
  });

export const adminResolveWebhookError = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("webhook_errors")
      .update({ resolved: true, resolved_at: new Date().toISOString(), resolved_by: context.userId })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminEmailHealth = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    const { data: rows } = await supabaseAdmin
      .from("email_log")
      .select("status")
      .gte("created_at", since);
    const counts = (rows ?? []).reduce<Record<string, number>>((acc, r) => {
      acc[r.status] = (acc[r.status] ?? 0) + 1;
      return acc;
    }, {});
    return { counts, total: rows?.length ?? 0 };
  });

// Web Vitals RUM summary: p75 per metric per path over a window.
export const adminWebVitalsSummary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ hours: z.number().int().min(1).max(720).default(24) }).parse(i ?? {}))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const since = new Date(Date.now() - data.hours * 3600 * 1000).toISOString();
    const { data: rows, error } = await supabaseAdmin
      .from("web_vitals")
      .select("name, value, rating, path")
      .gte("created_at", since)
      .limit(20000);
    if (error) throw new Error(error.message);
    // Aggregate p75 per (path, metric)
    const buckets = new Map<string, number[]>();
    for (const r of rows ?? []) {
      const key = `${r.path}|${r.name}`;
      const arr = buckets.get(key) ?? [];
      arr.push(Number(r.value));
      buckets.set(key, arr);
    }
    const rowsOut: Array<{ path: string; metric: string; p75: number; samples: number }> = [];
    for (const [k, arr] of buckets) {
      arr.sort((a, b) => a - b);
      const p75 = arr[Math.min(arr.length - 1, Math.floor(arr.length * 0.75))];
      const [path, metric] = k.split("|");
      rowsOut.push({ path, metric, p75: Math.round(p75 * 100) / 100, samples: arr.length });
    }
    rowsOut.sort((a, b) => (a.path === b.path ? a.metric.localeCompare(b.metric) : a.path.localeCompare(b.path)));
    return { rows: rowsOut, total: rows?.length ?? 0 };
  });


// ----- Failed email log + retry -----
export const adminListEmailFailures = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("email_log")
      .select("id, recipient_email, template, subject, status, error, booking_id, created_at")
      .in("status", ["failed", "queued", "pending_provider"])
      .order("created_at", { ascending: false })
      .limit(100);
    return { rows: data ?? [] };
  });

export const adminRetryEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { sendBookingEmail } = await import("./email.server");
    const { data: row } = await supabaseAdmin.from("email_log").select("template, booking_id, recipient_email").eq("id", data.id).single();
    if (!row?.booking_id) throw new Error("retry_not_supported");
    // Delete the failed row so the idempotency unique index allows a re-send
    await supabaseAdmin.from("email_log").delete().eq("id", data.id);
    await sendBookingEmail(row.booking_id, row.template as never, {}, { idempotencyKey: `retry:${data.id}` });
    return { ok: true };
  });

// ----- Cron run log -----
export const adminListCronRuns = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("cron_run_log")
      .select("id, job_name, status, details, duration_ms, created_at")
      .order("created_at", { ascending: false })
      .limit(50);
    return { rows: data ?? [] };
  });

// ----- Reprocess a webhook error (replays the original Stripe payload) -----
export const adminReprocessWebhook = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("webhook_errors").select("event_id").eq("id", data.id).single();
    if (!row?.event_id) throw new Error("not_found");
    // Allow Stripe to resend by clearing the dedupe row + marking error resolved.
    await supabaseAdmin.from("stripe_webhook_events").delete().eq("event_id", row.event_id);
    await supabaseAdmin.from("webhook_errors").update({
      resolved: true, resolved_at: new Date().toISOString(), resolved_by: context.userId,
    }).eq("id", data.id);
    return { ok: true, hint: "Replay the event from Stripe Dashboard → Developers → Events." };
  });

// ----- Admin role check (used by client-side route guards) -----
export const adminCheckRole = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    return { isAdmin: Boolean(data) };
  });
